/** 일별 근태 표 표시 헬퍼 — app.ts에서 추출. 포맷터·공휴일 판정은 주입. */

import { formatHoursHM } from "@hereby/core/date";

/** 날짜 라벨 — YYYY.MM.DD.(요일). 근태 표는 예외적으로 24시간/한국식 날짜 표기. */
export function attnDateLabel(date: unknown): string {
  const s = String(date || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return "-";
  }
  const [y, m, d] = s.split("-").map(Number);
  const dow = ["일", "월", "화", "수", "목", "금", "토"][
    new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  ];
  return `${y}.${String(m).padStart(2, "0")}.${String(d).padStart(2, "0")}.(${dow})`;
}

/** 날짜 빨간 톤 — 주말·공휴일(한국)·OFF(휴무 근무) 포함. */
export function attendanceRowRed(
  r: Record<string, unknown>,
  holidayName: (date: unknown) => string,
): boolean {
  if (r.isWeekend === true || holidayName(r.date) !== "") {
    return true;
  }
  const shiftName = String(r.shiftName || "").toLowerCase();
  return shiftName.includes("off") || shiftName.includes("휴무");
}

/** 야간 퇴근 표시 — 퇴근이 다음날이면 +N (예: "06:00 (+1)"). */
export function checkOutDayOffset(
  r: Record<string, unknown>,
  formatDateISO: (v: string) => string,
): number {
  const out = (r.actualCheckOut as string) || (r.checkOut as string) || null;
  if (!out) {
    return 0;
  }
  const outIso = formatDateISO(out) || String(out).slice(0, 10);
  const dateIso = String(r.date || "").slice(0, 10);
  if (!(outIso && dateIso)) {
    return 0;
  }
  const diff = Math.round((new Date(outIso).getTime() - new Date(dateIso).getTime()) / 86400000);
  return diff > 0 ? diff : 0;
}

/** 변경 내용 diff — 출퇴근 + 8개 구분 + 휴게 중 변경된 항목만. */
export function attnEditChanges(
  h: Record<string, unknown>,
  formatTime: (v: unknown) => string,
): { label: string; before: string; after: string }[] {
  const out: { label: string; before: string; after: string }[] = [];
  const t = (v: unknown) => formatTime(v) || "미입력";
  if (h.beforeCheckIn !== h.afterCheckIn) {
    out.push({ label: "출근", before: t(h.beforeCheckIn), after: t(h.afterCheckIn) });
  }
  if (h.beforeCheckOut !== h.afterCheckOut) {
    out.push({ label: "퇴근", before: t(h.beforeCheckOut), after: t(h.afterCheckOut) });
  }
  const numFields: { key: string; label: string }[] = [
    { key: "Regular", label: "일반" },
    { key: "Overtime", label: "연장" },
    { key: "Night", label: "야간" },
    { key: "NightOvertime", label: "야간연장" },
    { key: "HolidayRegular", label: "휴일일반" },
    { key: "HolidayOvertime", label: "휴일연장" },
    { key: "HolidayNight", label: "휴일야간" },
    { key: "HolidayNightOvertime", label: "휴일야간연장" },
    { key: "Break", label: "휴게" },
  ];
  for (const f of numFields) {
    const before = Number(h[`before${f.key}`] ?? 0);
    const after = Number(h[`after${f.key}`] ?? 0);
    if (Math.abs(before - after) > 0.001) {
      // h:mm 표기 (실근로시간 정산 규칙, 2026-07) — 예: 8:00 → 7:30
      out.push({ label: f.label, before: formatHoursHM(before), after: formatHoursHM(after) });
    }
  }
  return out;
}
